import "amazon-connect-streams";

/**
 * Matches an Amazon Connect **quick connect** (transfer-destination) ARN, e.g.
 * `arn:aws:connect:us-east-1:123456789012:instance/<id>/transfer-destination/<id>`.
 *
 * Only transfer-destination ARNs are accepted: `addConnection`/`createAdditionalConnection` rejects a
 * raw `:queue/` resource ARN with `InvalidStateException` (400), so we fail fast here with a clear
 * message rather than letting a queue ARN surface as a confusing conference failure mid-call.
 */
const CONNECT_QUICK_CONNECT_ARN =
    /^arn:aws:connect:[a-z0-9-]+:\d{12}:instance\/[0-9a-fA-F-]+\/transfer-destination\/[0-9a-fA-F-]+$/;

/**
 * Builds an Amazon Connect Streams `Endpoint` from a known quick-connect (transfer-destination) ARN so
 * a conference can be started with `contact.addConnection(...)` **without** discovering the endpoint
 * through the agent's routing profile (`agent.getEndpoints`).
 *
 * The Streams `Endpoint` class exposes no public constructor for ARNs (only `byPhoneNumber`), so we
 * hand-roll the object it consumes — `addConnection`/`addParticipant` key off `endpointARN` + `type`,
 * and the remaining fields are safely empty. This mirrors the proven pattern in `uxp-ccp`.
 *
 * @param endpointARN - a Connect transfer-destination (quick connect) ARN
 * @throws if `endpointARN` is not a well-formed Connect quick-connect ARN
 */
export function buildEndpointFromArn(endpointARN: string): connect.Endpoint {
    if (!CONNECT_QUICK_CONNECT_ARN.test(endpointARN)) {
        throw new Error(`Not a valid Amazon Connect quick-connect ARN: "${endpointARN}"`);
    }

    return {
        endpointARN,
        endpointId: "",
        type: connect.EndpointType.QUEUE,
        name: "",
        phoneNumber: "",
        agentLogin: "",
        queue: "",
    };
}
